import React from 'react';
import HighCharts from 'highcharts';
import { Immutable } from 'nuclear-js';
import { Checkbox } from 'react-bootstrap';
import PropTypes from 'prop-types';
import CreateReactClass from 'create-react-class';

import { MIN_RIT, MAX_RIT } from 'resources/common-values';

import InitialChartOptions from './initial-chart-options.json';
import QuintileOptions from './quintile-options.json';
import PlotLineOptions from './plot-line-options.json';
import GrowthGoalOptions from './goal-series-options.json';
import TestSeriesOptions from './test-series-options.json';
import TypicalScoreOptions from './typical-score-options.json';
import ProjectionSeriesOptions from './projections-options.json';
import CurrentGoalOptions from './current-goal-options.json';
import LinkingStudyOptions from './linking-study-options.json';
import NoDataOptions from './no-data-options.json';
import ChartUtils from 'utils/chart';
import GlobalChartUtils from 'utils/chart';

const seriesIndexMap = new Map();
const seriesIndexKeys = {
    goalSeries: 'GOAL_SERIES',
    quintile1: 'QUINTILE1',
    quintile2: 'QUINTILE2',
    quintile3: 'QUINTILE3',
    quintile4: 'QUINTILE4',
    quintile5: 'QUINTILE5',
    quintileNoData: 'QUINTILE_NO_DATA',
    projections: 'PROJECTIONS',
    typicalScore: 'TYPICAL_SCORE',
    currentGoal: 'CURRENT_GOAL',
    testEvents: 'TEST_EVENTS',
    linkingStudiesIndex: 'LINKING_STUDIES_INDEX', // 11
};
// no idea why this is needed to clear the title
const initOptions = Object.freeze({ ...InitialChartOptions, title: { text: '' } });
export default CreateReactClass({
    displayName: 'GrowthGoalsChart',

    propTypes: {
        chartContainerId: PropTypes.string.isRequired,
        collegeLinkingStudies: PropTypes.array,
        currentRit: PropTypes.number,
        dataLoaded: PropTypes.bool.isRequired,
        growthGoals: PropTypes.instanceOf(Immutable.List),
        growthGoalYn: PropTypes.bool.isRequired,
        growthProjections: PropTypes.instanceOf(Immutable.List),
        hasNoData: PropTypes.bool.isRequired,
        measurementScaleID: PropTypes.number,
        ritQuintiles: PropTypes.instanceOf(Immutable.Map),
        subject: PropTypes.instanceOf(Immutable.Map).isRequired,
        targetTermNumber: PropTypes.number,
        terms: PropTypes.instanceOf(Immutable.List).isRequired, // must be sorted
        testEvents: PropTypes.instanceOf(Immutable.List).isRequired,
        testToProjectFromForCurrentTerm: PropTypes.instanceOf(Immutable.Map),
        stateLinkingStudy: PropTypes.instanceOf(Immutable.Map),
        studentBid: PropTypes.string.isRequired,
    },

    
    chart: null,

    getInitialState() {
        let showCollegeStudyMap = Immutable.Map();
        GlobalChartUtils.collegeLinkingStudyTypes.forEach((lsKey) => {
            showCollegeStudyMap = showCollegeStudyMap.set(lsKey, false);
        });
        return {
            showCollegeStudy: showCollegeStudyMap,
            showStateStudy: false,
        };
    },

    //<editor-fold desc="react life-cycle">
    componentDidMount() {
        this.chart = this.createChart();
        this.updateChart();
    },

    shouldComponentUpdate(nextProps, nextState) {
        const studentChanged = this.props.studentBid != nextProps.studentBid;

        const growthGoalChanged =
            (nextProps.growthGoals && !nextProps.growthGoals.equals(this.props.growthGoals)) ||
            (!nextProps.growthGoals && this.props.growthGoals);

        const justLoaded = !this.props.dataLoaded && nextProps.dataLoaded;

        return (
            studentChanged ||
            growthGoalChanged ||
            nextProps.currentRit !== this.props.currentRit ||
            nextState.showCollegeStudy !== this.state.showCollegeStudy ||
            nextState.showStateStudy !== this.state.showStateStudy ||
            nextProps.targetTermNumber !== this.props.targetTermNumber ||
            justLoaded
        );
    },

    componentDidUpdate(props) {
        this.updateChart();
    },

    componentWillUnmount() {
        if (this.chart) {
            try {
                this.chart.destroy();
            } catch (e) {
                // Control the failure
                console.error(e);
            }
        }
    },
    //</editor-fold>

    createChart() {

        console.log("In Create chat --------------------")
        let series = [];
        const pushSeriesItem = (item, indexKey) => {
            // push the item onto the series array, and save the index in the seriesIndexMap
            // so we can retrieve it via its key later.
            seriesIndexMap.set(indexKey, series.length);
            series.push(item);
        };

        seriesIndexMap.clear();
        pushSeriesItem(ChartUtils.createDataSeries(GrowthGoalOptions), seriesIndexKeys.goalSeries);
        pushSeriesItem(QuintileOptions.quintile1, seriesIndexKeys.quintile1);
        pushSeriesItem(QuintileOptions.quintile2, seriesIndexKeys.quintile2);
        pushSeriesItem(QuintileOptions.quintile3, seriesIndexKeys.quintile3);
        pushSeriesItem(QuintileOptions.quintile4, seriesIndexKeys.quintile4);
        pushSeriesItem(QuintileOptions.quintile5, seriesIndexKeys.quintile5);
        pushSeriesItem(QuintileOptions.noData, seriesIndexKeys.quintileNoData);
        pushSeriesItem(ChartUtils.createDataSeries(ProjectionSeriesOptions), seriesIndexKeys.projections);
        pushSeriesItem(ChartUtils.createDataSeries(TypicalScoreOptions), seriesIndexKeys.typicalScore);
        pushSeriesItem(ChartUtils.createDataSeries(CurrentGoalOptions), seriesIndexKeys.currentGoal);
        pushSeriesItem(ChartUtils.createDataSeries(TestSeriesOptions), seriesIndexKeys.testEvents);
        //Don't push any linking studies yet, but save the slot where they will start
        seriesIndexMap.set(seriesIndexKeys.linkingStudiesIndex, series.length);

        //     don't want to render the chart
        //     until the container is there with the proper ID
        return __UNIT_TEST__ ? null : HighCharts.chart(this.props.chartContainerId, { ...InitialChartOptions, series });
    },

    findTargetTermIndex() {
        if (!this.props.terms || !this.props.targetTermNumber) {
            return null;
        }

        return this.props.terms.findIndex((t) => t.get('termNumber') === this.props.targetTermNumber);
    },

    getChartBottom(series) {
        const min = ChartUtils.findMinForSeries(series);

        // if min === MAX_RIT, no valid min could be found
        return min === MAX_RIT ? MIN_RIT : min - 10;
    },

    getChartTop(series) {
        const max = ChartUtils.findMaxForSeries(series);

        // if max === MIN_RIT, no valid max could be found
        return max === MIN_RIT ? MAX_RIT : max + 10;
    },

    getStudyForKey(lsKey) {
        return this.props.collegeLinkingStudies.find((item) => {
            return item.get('study') === lsKey;
        });
    },

    setSeriesVisibility(isVisible) {
        if (this.chart && seriesIndexMap) {
            for (let seriesKey of seriesIndexMap.values()) {
                const series = this.chart.series[seriesKey];
                if (series && series.visible !== isVisible) {
                    isVisible ? series.show() : series.hide();
                }
            }
        }
    },

    updateChart(chartUtils = ChartUtils) {
        console.log("In updateChart ------------------------")
        if (this.chart) {
            if (this.props.hasNoData) {
                this.setSeriesVisibility(false);
                this.chart.update(NoDataOptions, true);
                this.chart.xAxis[0].setCategories(null, false);
                return;
            } else if (!this.props.dataLoaded) {
                this.setSeriesVisibility(false);
                this.chart.showLoading('Loading....');
                this.chart.update(NoDataOptions, true);
                this.chart.xAxis[0].setCategories(null, false);
                return;
            }
            this.setSeriesVisibility(true);
            this.chart.update({ ...initOptions }, true);

            let seriesToDetermineChartTopAndBottom = [];
            const growthGoalData = chartUtils.populateGrowthGoalSeriesData(
                this.props.terms,
                this.props.growthGoals,
                TestSeriesOptions
            );
            const testEventData = chartUtils.populateRITScoreSeries(
                this.props.terms,
                this.props.subject.get('bid'),
                this.props.testEvents,
                null,
                this.props.measurementScaleID
            );
            const quintileSeries = chartUtils.populateRitQuintileSeries(this.props.terms, this.props.ritQuintiles);

            this.chart.series[seriesIndexMap.get(seriesIndexKeys.goalSeries)].setData(growthGoalData, false);
            if (growthGoalData) {
                seriesToDetermineChartTopAndBottom = seriesToDetermineChartTopAndBottom.concat(growthGoalData);
            }
            this.chart.series[seriesIndexMap.get(seriesIndexKeys.quintile1)].setData(
                quintileSeries.quintile1Data,
                false
            );
            this.chart.series[seriesIndexMap.get(seriesIndexKeys.quintile2)].setData(
                quintileSeries.quintile2Data,
                false
            );
            this.chart.series[seriesIndexMap.get(seriesIndexKeys.quintile3)].setData(
                quintileSeries.quintile3Data,
                false
            );
            this.chart.series[seriesIndexMap.get(seriesIndexKeys.quintile4)].setData(
                quintileSeries.quintile4Data,
                false
            );
            this.chart.series[seriesIndexMap.get(seriesIndexKeys.quintile5)].setData(
                quintileSeries.quintile5Data,
                false
            );
            this.chart.series[seriesIndexMap.get(seriesIndexKeys.quintileNoData)].setData(quintileSeries.noData, false);

            const targetTermIndex = this.findTargetTermIndex();
            chartUtils.addPlotLine(targetTermIndex + 1, this.chart, PlotLineOptions, 'TARGET_TERM');

            this.chart.xAxis[0].setCategories(chartUtils.populateCategoryNames(this.props.terms));

            let projectionSeries = null;
            let showProjectionsInLegend = false;
            if (this.props.growthGoalYn) {
                projectionSeries = chartUtils.populateProjectionSeries(
                    this.props.terms,
                    this.props.testToProjectFromForCurrentTerm,
                    this.props.growthProjections
                );
                showProjectionsInLegend = true;

                if (projectionSeries) {
                    seriesToDetermineChartTopAndBottom = seriesToDetermineChartTopAndBottom.concat(projectionSeries);
                }
            }
            this.chart.series[seriesIndexMap.get(seriesIndexKeys.projections)].setData(projectionSeries, false);
            this.chart.series[seriesIndexMap.get(seriesIndexKeys.projections)].update(
                { showInLegend: showProjectionsInLegend },
                false
            );

            let typicalScoreSeries = null;
            let showTypicalScoreInLegend = false;
            if (!this.props.growthGoalYn) {
                typicalScoreSeries = chartUtils.populateTypicalScoreSeries(this.props.terms, this.props.ritQuintiles);
                if (typicalScoreSeries) {
                    seriesToDetermineChartTopAndBottom = seriesToDetermineChartTopAndBottom.concat(typicalScoreSeries);
                }
                showTypicalScoreInLegend = true;
            }
            this.chart.series[seriesIndexMap.get(seriesIndexKeys.typicalScore)].setData(typicalScoreSeries, false);
            this.chart.series[seriesIndexMap.get(seriesIndexKeys.typicalScore)].update(
                { showInLegend: showTypicalScoreInLegend },
                false
            );

            const currentGoalData = chartUtils.populateCurrentGoalData(
                this.props.terms,
                this.props.targetTermNumber,
                this.props.testToProjectFromForCurrentTerm,
                this.props.currentRit,
                TestSeriesOptions,
                this.props.growthGoals
            );
            this.chart.series[seriesIndexMap.get(seriesIndexKeys.currentGoal)].setData(currentGoalData, false);
            if (currentGoalData) {
                seriesToDetermineChartTopAndBottom = seriesToDetermineChartTopAndBottom.concat(currentGoalData);
            }

            let showTestEventsInLegend = false;
            if (this.props.growthGoalYn) {
                showTestEventsInLegend = true;
                if (testEventData) {
                    seriesToDetermineChartTopAndBottom = seriesToDetermineChartTopAndBottom.concat(testEventData);
                }
            }
            this.chart.series[seriesIndexMap.get(seriesIndexKeys.testEvents)].setData(testEventData, false);
            this.chart.series[seriesIndexMap.get(seriesIndexKeys.testEvents)].update(
                { showInLegend: showTestEventsInLegend },
                false
            );
            const chartTop = this.getChartTop(seriesToDetermineChartTopAndBottom);
            const chartBottom = this.getChartBottom(seriesToDetermineChartTopAndBottom);

            //Evaluate and Update Linking Study Series
            let linkingStudyIndex = seriesIndexMap.get(seriesIndexKeys.linkingStudiesIndex);

            //Used for data label correction
            let collegeLinkingStudyIndices = [];
            let stateLinkingStudiesIndexStart = null,
                stateLinkingStudiesIndexEnd = null,
                stateLinkingStudiesDataIndex = null;

            if (this.props.stateLinkingStudy || this.props.collegeLinkingStudies) {
                this.removeLinkingStudySeries(this.chart);
                const stateLinkingStudySeries = chartUtils.populateLinkingStudySeries(
                    this.props.stateLinkingStudy,
                    this.props.terms,
                    chartTop,
                    false,
                    LinkingStudyOptions,
                    chartBottom
                );
                if (this.props.collegeLinkingStudies) {
                    GlobalChartUtils.collegeLinkingStudyTypes.forEach((lsKey) => {
                        const collegeLinkingStudy = this.getStudyForKey(lsKey);
                        if (collegeLinkingStudy) {
                            const collegeLinkingStudySeries = chartUtils.populateLinkingStudySeries(
                                collegeLinkingStudy,
                                this.props.terms,
                                chartTop,
                                false,
                                LinkingStudyOptions,
                                chartBottom
                            );
                            if (collegeLinkingStudySeries && this.state.showCollegeStudy.get(lsKey)) {
                                const collegeLinkingStudiesIndexStart = linkingStudyIndex;
                                let collegeLinkingStudiesDataIndex = null;
                                collegeLinkingStudySeries.forEach((cls) => {
                                    this.chart.addSeries(cls, false);
                                    if (!collegeLinkingStudiesDataIndex) {
                                        //Assumes all series data live at the same index, returns the index where y is not null
                                        collegeLinkingStudiesDataIndex = cls.data.findIndex((item) => item.y);
                                    }
                                    linkingStudyIndex++;
                                });
                                const collegeLinkingStudiesIndexEnd = linkingStudyIndex - 1;

                                collegeLinkingStudyIndices.push({
                                    startIndex: collegeLinkingStudiesIndexStart,
                                    dataIndex: collegeLinkingStudiesDataIndex,
                                    endIndex: collegeLinkingStudiesIndexEnd,
                                });
                            }
                        }
                    });
                }

                if (stateLinkingStudySeries && this.state.showStateStudy) {
                    stateLinkingStudiesIndexStart = linkingStudyIndex;
                    stateLinkingStudySeries.forEach((sls) => {
                        this.chart.addSeries(sls, false);
                        if (!stateLinkingStudiesDataIndex) {
                            //Assumes all series data live at the same index, returns the index where y is not null
                            stateLinkingStudiesDataIndex = sls.data.findIndex((item) => item.y);
                        }
                        linkingStudyIndex++;
                    });
                    stateLinkingStudiesIndexEnd = linkingStudyIndex - 1;
                }
            }

            this.chart.yAxis[0].setExtremes(chartBottom, chartTop, false);

            let termsCount = this.props.terms.size;
            if (
                this.props.stateLinkingStudy &&
                this.state.showStateStudy &&
                this.props.terms.get(this.props.terms.size - 1).get('termNumber') ===
                    this.props.stateLinkingStudy.get('termNumber')
            ) {
                termsCount++;
            }

            let minToggle = false;

            if (this.props.collegeLinkingStudies) {
                const firstTermNumber = this.props.terms.get(0).get('termNumber');

                for (let i = 0; i < GlobalChartUtils.collegeLinkingStudyTypes.length; i++) {
                    if (this.state.showCollegeStudy.get(GlobalChartUtils.collegeLinkingStudyTypes[i])) {
                        const collegeLinkingStudy = this.getStudyForKey(GlobalChartUtils.collegeLinkingStudyTypes[i]);
                        if (firstTermNumber === collegeLinkingStudy.get('termNumber')) {
                            minToggle = true;
                            break;
                        }
                    }
                }
            }

            const newMin = minToggle ? 0 : 1;
            this.chart.xAxis[0].setExtremes(newMin, termsCount, false);
            this.chart.hideLoading();
            this.chart.redraw();

            // Fix overlapping data labels for linking studies (call AFTER chart.redraw()!)
            collegeLinkingStudyIndices.forEach((indices) => {
                GlobalChartUtils.fixColumnChartDataLabels(
                    this.chart,
                    indices.startIndex,
                    indices.endIndex,
                    indices.dataIndex
                );
            });
            ChartUtils.fixColumnChartDataLabels(
                this.chart,
                stateLinkingStudiesIndexStart,
                stateLinkingStudiesIndexEnd,
                stateLinkingStudiesDataIndex
            );
        }
    },

    removeLinkingStudySeries(chart) {
        if (!chart || !chart.series) return;

        // build up the list to remove and remove them later as the immediate removal screws up the foreach
        let seriesToRemove = [];
        chart.series.forEach((s) => {
            if (s.type == 'column') {
                seriesToRemove.push(s);
            }
        });
        seriesToRemove.forEach((s) => s.remove());

        return chart;
    },

    renderPercentileBands() {
        console.log(" (this.props.dataLoaded ::"+ this.props.dataLoaded +
        " this.props.hasNoData ::"+this.props.hasNoData)
        if (this.props.dataLoaded && !this.props.hasNoData) {
            return (
                <ul className="quintile-legend">
                    <li key="title" className="title">
                        Percentile Bands
                    </li>
                    <li key="quintile1" className="quintile1">
                        1-20
                    </li>
                    <li key="quintile2" className="quintile2">
                        21-40
                    </li>
                    <li key="quintile3" className="quintile3">
                        41-60
                    </li>
                    <li key="quintile4" className="quintile4">
                        61-80
                    </li>
                    <li key="quintile5" className="quintile5">
                        81-100
                    </li>
                    <li key="no-data" className="no-data">
                        no data
                    </li>
                </ul>
            );
        } else {
            return null;
        }
    },

    handleCheckCollege(lsKey, e) {
        this.setState({
            showCollegeStudy: this.state.showCollegeStudy.set(lsKey, !this.state.showCollegeStudy.get(lsKey)),
        });
    },

    handleCheckState(e) {
        this.setState({ showStateStudy: !this.state.showStateStudy });
    },

    renderLinkingStudyLegend() {
        if (
            !this.props.dataLoaded ||
            this.props.hasNoData ||
            (!this.props.collegeLinkingStudies && !this.props.stateLinkingStudy)
        ) {
            return null;
        }

        let collegeCheckBoxes = null;
        if (this.props.collegeLinkingStudies) {
            GlobalChartUtils.collegeLinkingStudyTypes.forEach((lsKey) => {
                const collegeLinkingStudySeries = this.props.collegeLinkingStudies.find((item) => {
                    return item.get('study') === lsKey;
                });

                if (collegeLinkingStudySeries) {
                    if (!collegeCheckBoxes) {
                        collegeCheckBoxes = [];
                    }
                    collegeCheckBoxes.push(
                        <li className="nwea-checkbox-graph" key={`college-checkbox-${lsKey.toLowerCase()}`}>
                            <Checkbox
                                id={`check-college-linking-study-${lsKey.toLowerCase()}`}
                                key={`check-college-linking-study-${lsKey.toLowerCase()}`}
                                checked={this.state.showCollegeStudy.get(lsKey)}
                                onChange={(e) => this.handleCheckCollege(lsKey, e)}
                            />
                            {lsKey}
                        </li>
                    );
                }
            });
        }

        const stateCheckBox = this.props.stateLinkingStudy ? (
            <li className="nwea-checkbox-graph" key="state-checkbox">
                <Checkbox
                    id="check-state-linking-study"
                    key="check-state-linking-study"
                    checked={this.state.showStateStudy}
                    onChange={this.handleCheckState}
                />
                {this.props.stateLinkingStudy.get('study')}
            </li>
        ) : null;

        return (
            <ul className="key-linking-studies list-inline list-unstyled" key="linking-study-legend">
                <li className="linking-studies-legend" key="show linking studies">
                    Show linking studies:
                </li>
                {collegeCheckBoxes}
                {stateCheckBox}
            </ul>
        );
    },

    render() {
        const percentileBands = this.renderPercentileBands();
        const className = 'chart-container' + (this.props.hasNoData ? ' no-data' : '');

        return (
            <div className={className}>
                <div id={this.props.chartContainerId} key="growth-goal-chart" />
                {this.renderLinkingStudyLegend()}
                {percentileBands}
            </div>
        );
    },
});
